/* UI-DESIGN: Open Cases — every murder mystery the signed-in user is part
   of (hosting, playing, or RSVP'ed), each linking to its game page. */
import { Link } from "react-router-dom";
import clsx from "clsx";
import { useGetMyGamesQuery } from "../../reducers/apiSlice";
import { useLoginState } from "../../hooks/login";
import { useOpenLoginModal } from "../../components/Modals/loginModalContext";
import type { Game } from "../../types/game";
import { GameState } from "../../types/game";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import StatePanel from "../../components/lightbox/StatePanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import { initialTypeface } from "../../components/lightbox/typefaces";
import styles from "./OpenCases.module.scss";

const RSVP_GOING = "going";
const RSVP_DROPPED = "dropped";

const GAME_STATE_LABELS: Record<string, string> = {
  [GameState.INVITING]: "Invites open",
  [GameState.INVITES_CLOSED]: "Invites closed",
  [GameState.PRE_GAME]: "Roles sent",
  [GameState.IN_PROGRESS]: "In progress",
};

function myStatus(game: Game): { label: string; tone: "host" | "going" | "dropped" | "invited" } {
  if (game.is_host) return { label: "Hosting", tone: "host" };
  if (game.rsvp?.status === RSVP_GOING) return { label: "Going", tone: "going" };
  if (game.rsvp?.status === RSVP_DROPPED) return { label: "Can’t make it", tone: "dropped" };
  return { label: "Invited", tone: "invited" };
}

function CaseCard({ game }: { game: Game }) {
  const status = myStatus(game);
  const attending = (game.players ?? []).filter((p) => p.attendance).length;
  const imageUrl = game.mystery?.image_url || null;

  return (
    <li className={styles.card}>
      <Link to={`/game/${game.objectId}`} className={styles.cardLink}>
        <div className={styles.cardImageWrap}>
          {imageUrl ? (
            <img
              className={styles.cardImage}
              src={imageUrl}
              alt=""
              loading="lazy"
            />
          ) : (
            <div className={styles.cardImageFallback} aria-hidden />
          )}
          <span className={clsx(styles.statusChip, styles[`status_${status.tone}`])}>
            {status.label}
          </span>
        </div>
        <div className={styles.cardBody}>
          <h2 className={styles.cardTitle}>{game.name || "Untitled party"}</h2>
          <p className={styles.cardMystery}>{game.mystery?.name ?? "Mystery"}</p>
          <p className={styles.cardMeta}>
            {GAME_STATE_LABELS[game.game_state] ?? game.game_state}
            {", "}
            {attending} attending
          </p>
          <span className={styles.cardCta}>
            View case <span className="ui-arrow" aria-hidden>→</span>
          </span>
        </div>
      </Link>
    </li>
  );
}

const OpenCases = () => {
  const { isLoggedIn } = useLoginState();
  const openLoginModal = useOpenLoginModal();
  /* UI-DESIGN: my-games needs auth — skip until the user is logged in so we
     don't bounce anonymous visitors to /login */
  const { data: games, isLoading, isError, isFetching, refetch } = useGetMyGamesQuery(undefined, {
    skip: !isLoggedIn, refetchOnMountOrArgChange: true,
  });

  const openCases = games ?? [];

  return (
    <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={() => undefined}>
    {/* UI-DESIGN: show this task page immediately, without staggered entrance fades. */}
    <main className={styles.page}>
      <h1 className={styles.title}>Open Cases</h1>
      <p className={styles.subtitle}>
        The parties you’re hosting and the mysteries you’re part of.
      </p>

      {!isLoggedIn ? (
        <StatePanel className={styles.emptyPanel}>
          <p className={styles.emptyText}>
            Log in to see the mysteries you&apos;re part of.
          </p>
          <LightboxButton onPress={() => openLoginModal()}>Log in</LightboxButton>
        </StatePanel>
      ) : isLoading || (isFetching && !games) ? (
        /* UI-DESIGN: a quiet state panel avoids flashing a full grid before an empty result. */
        <StatePanel className={styles.emptyPanel} aria-busy="true" aria-label="Loading your cases">
          <p className={styles.emptyText} role="status">Opening your cases…</p>
        </StatePanel>
      ) : isError ? (
        <StatePanel className={styles.emptyPanel}>
          <h2 className={styles.stateTitle}>Your cases couldn’t be opened</h2>
          <p className={styles.emptyText} role="alert">Try again to pick up where you left off.</p>
          <LightboxButton onPress={() => { void refetch(); }} isDisabled={isFetching}>{isFetching ? "Opening…" : "Try again"}</LightboxButton>
        </StatePanel>
      ) : openCases.length === 0 ? (
        <StatePanel className={styles.emptyPanel}>
          <p className={styles.emptyText}>
            No open cases yet. Host a party or RSVP to an invite and it will
            show up here.
          </p>
          <Link to="/mysteries" className={styles.emptyButton}>
            Browse Mysteries
          </Link>
        </StatePanel>
      ) : (
        <>
          <p className={styles.sectionLabel}>
            {openCases.length} open{" "}
            {openCases.length === 1 ? "case" : "cases"}
          </p>
          <ul className={styles.grid}>
            {openCases.map((game) => (
              <CaseCard key={game.objectId} game={game} />
            ))}
          </ul>
        </>
      )}
    </main>
    </LightboxSurface>
  );
};

export default OpenCases;
